import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Close, Warning } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { H4 } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { ExternalRouteButton } from '@/components/RouteButton/RouteButton';
import { NotificationPermissions } from '@/enums/notificationPermissions.enum';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getCookie, setCookie } from '@/redux/modules/cookies';
import {
    forceEnableNotificationsBannerDismiss,
    getForceEnableBanner,
    getPermission,
    requestBrowserPermission,
} from '@/redux/modules/notification';
import { useTrackBrowserNotificationsClicked } from '@/utils/amplitude/analytics/notifications';
import { enableBrowserNotificationsUrl } from '@/utils/urls';
const COOKIE_MAX_AGE = 86400 * 365; // 365 days
const COOKIE_NAME = 'dismissableNotificationBanner';
const COOKIE_BODY = 'dismissed';

const DismissableGetWebNotificationsBanner = () => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const { pathname } = useLocation();
    const isCheckoutPage = pathname.includes('/checkout/');
    const biddingConsole = pathname.includes('/console/');
    const forceEnableBanner = useAppSelector(getForceEnableBanner);
    const permission = useAppSelector(getPermission);
    const dismissed = useAppSelector((state) => getCookie(state, COOKIE_NAME)) === COOKIE_BODY;
    const trackBrowserNotificationsClicked = useTrackBrowserNotificationsClicked();

    const setCookieChecked = React.useCallback((): void => {
        dispatch(
            setCookie({
                maxAge: COOKIE_MAX_AGE,
                name: COOKIE_NAME,
                value: COOKIE_BODY,
            })
        );
        dispatch(forceEnableNotificationsBannerDismiss());
    }, [dispatch]);

    const onClickEnableNotifications = () => {
        dispatch(requestBrowserPermission());
        trackBrowserNotificationsClicked();
    };

    if (biddingConsole || isCheckoutPage || permission === '') {
        return null;
    }

    if (!forceEnableBanner) {
        if (permission === NotificationPermissions.Granted || Boolean(dismissed)) {
            return null;
        }
    }

    const disallowedNotifications = permission === NotificationPermissions.Denied;

    return (
        <StyledBanner
            data-testid="webNotificationsBanner"
            id="webNotificationsBanner"
        >
            {disallowedNotifications ? (
                <StyledInnerContainer
                    data-testid="webNotificationsDenied"
                    id="webNotificationsDenied"
                >
                    <Warning
                        className="mr-xs text-warning-400"
                        size="sm"
                        style="fill"
                    />
                    <H4 color="inverse">
                        <FormattedMessage id="notifications.modify_your_browser_preferences" />
                    </H4>
                    <ExternalRouteButton
                        rel="noopener"
                        size="sm"
                        style="inverse"
                        target="_blank"
                        to={enableBrowserNotificationsUrl}
                        variant="outlined"
                    >
                        <FormattedMessage id="top_learn_more" />
                    </ExternalRouteButton>
                </StyledInnerContainer>
            ) : (
                <StyledInnerContainer
                    data-testid="webNotificationsDefault"
                    id="webNotificationsDefault"
                >
                    <H4 color="inverse">
                        <FormattedMessage id="notifications.get_notifications_when_outbid" />
                    </H4>
                    <Button
                        onClick={onClickEnableNotifications}
                        size="sm"
                        style="inverse"
                        variant="outlined"
                    >
                        {formatMessage({
                            id: 'notifications.enable_browser_notifications',
                        })}
                    </Button>
                </StyledInnerContainer>
            )}
            <IconButton
                className="absolute right-xs top-xs z-10"
                icon={Close}
                onClick={setCookieChecked}
                size="sm"
                style="inverse"
            />
        </StyledBanner>
    );
};

export default DismissableGetWebNotificationsBanner;

const StyledBanner = styled.div`
    width: 100%;
    display: flex;
    background-color: ${({ theme }) => theme.colors.blue200};
    height: 62px;
    padding: 0 20px;
    position: sticky;
    top: 0;
    z-index: 1000;

    @media (max-width: 975px) {
        padding-right: 40px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        display: none;
    }
`;

const StyledInnerContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
`;
